import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MobilePhoneNumberService, NationalCodeService } from 'ngx-persian';
import { catchError, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { AccountService } from 'src/app/services/account.service';
import { MapService } from 'src/app/services/map.service';
import { OtherService } from 'src/app/services/other.service';
import Swal from 'sweetalert2';

@Component({

  selector: 'app-specialist-registration',

  templateUrl: './specialist-registration.component.html',

  styleUrls: ['./specialist-registration.component.css']

})

export class SpecialistRegistrationComponent implements OnInit {

  private persianRegex = /^[\u0600-\u06FF\s]$/;

  showMap: boolean = false;

  isSubmitDisabled = false;

  formGroup = new FormGroup({
    strName: new FormControl('', [Validators.required]),
    strFamily: new FormControl('', [Validators.required]),
    strID: new FormControl('', [Validators.required, this.validateStrID()]),
    strMobile: new FormControl('', [Validators.required, this.validateStrMobile()]),
    strTel: new FormControl('', [Validators.required]),
    strVIN: new FormControl(''),
    strLicenseNo: new FormControl(''),
    tiGPSType: new FormControl(null, [Validators.required]),
    strCity: new FormControl(null, [Validators.required]),
    strLicenseImage: new FormControl(''),
    strAddress: new FormControl(''),
    strComment: new FormControl(''),
    plak4: new FormControl('', [Validators.required, Validators.maxLength(2), Validators.pattern(/^[1-9][0-9]$/)]),
    plak3: new FormControl('', [Validators.required, Validators.maxLength(3), Validators.pattern(/^[1-9][0-9][0-9]$/)]),
    plak2: new FormControl('', [Validators.required]),
    plak1: new FormControl('', [Validators.required, Validators.maxLength(2), Validators.pattern(/^[1-9][0-9]$/)]),
  });

  coordinates = {
    fLat: 0,
    fLon: 0
  };

  showMapDialog = false;

  carTypes$ = this.accountService.tankerGPSType().pipe(
    map((response: string) => JSON.parse(response)),
    catchError(() => of(false)),
  );

  states$ = this.accountService.GetState().pipe(
    map((response: string) => JSON.parse(response)),
    catchError(() => of(false)),
  );

  cities$: Observable<any>;

  unSubscribeAll$ = new Subject<void>();

  constructor(
    private accountService: AccountService,
    private otherService: OtherService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private mapService: MapService,
    private nationalCodeService: NationalCodeService,
    private mobilePhoneNumberService: MobilePhoneNumberService,
  ) { }

  ngOnInit(): void {
    if (this.showMap) {
      this.formGroup.get('strAddress').addValidators(Validators.required);
    }

    const strMobile = this.activatedRoute.snapshot.queryParamMap.get('mobile');
    const isValid = this.mobilePhoneNumberService.isValidPhoneNumber(strMobile);

    if (isValid) {
      const strMobileFormControl = this.formGroup.get('strMobile');
      strMobileFormControl.setValue(strMobile);
      strMobileFormControl.disable();
    }
  }

  async submit() {

    if (this.formGroup.invalid || this.formGroup.pristine || this.isSubmitDisabled) {
      return;
    }

    const confirmDialog = await Swal.fire({
      title: 'آیا از ثبت نام اطمینان دارید؟',
      icon: 'warning',
      showCancelButton: true,
      showConfirmButton: true,
      reverseButtons: true,
      confirmButtonColor: '#10b981',
      confirmButtonText: 'تایید',
      cancelButtonText: 'انصراف',
    });

    if (!confirmDialog.isConfirmed) {
      return;
    }

    this.isSubmitDisabled = true;

    const formValue = this.formGroup.getRawValue();

    const strVehicleNo = `${formValue.plak1} ${formValue.plak2} ${formValue.plak3} | ${formValue.plak4}`;
    delete formValue.plak1;
    delete formValue.plak2;
    delete formValue.plak3;
    delete formValue.plak4;

    const model = {
      ...this.coordinates,
      ...formValue,
      strVehicleNo,
      iSystemUser: this.otherService.specialistUser.iSystemUser,
      strSession: this.otherService.specialistUser.strSession
    };

    this.accountService.specialistRegistration(model).pipe(
      catchError(() => of(false)),
      takeUntil(this.unSubscribeAll$),
    ).subscribe((response: string | false) => {
      this.isSubmitDisabled = false;

      if (response) {
        let result = JSON.parse(response);
        if (Array.isArray(result)) result = result[0];

        if (result.iResult === 0) {
          return this.accountService.toastSwal('error', result.strError);
        }

        this.accountService.toastSwal('success', `عملیات با موفقیت انجام شد`);
        this.router.navigateByUrl('/specialist-verification');
      } else {
        this.accountService.toastSwal('error', 'خطایی رخ داد');
      }
    });
  }

  show_Map() {
    this.mapService.getCurrentLocation().then(coords => {
      this.coordinates.fLon = coords.longitude;
      this.coordinates.fLat = coords.latitude;
    }).finally(() => this.showMapDialog = true);
  }

  submitMap(event: any) {
    this.formGroup.get('strAddress').setValue(event.strText);

    this.coordinates.fLon = event.longitude;
    this.coordinates.fLat = event.latitude;

    this.showMapDialog = false;
  }

  validateStrMobile(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlValue = control.value;
      const isValid = this.mobilePhoneNumberService.isValidPhoneNumber(controlValue);

      return isValid ? null : { mobilePhoneNumberWrong: true };
    };
  }

  validateStrID(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlValue = control.value;
      // const isValid = this.nationalCodeService.isValid(controlValue);
      const isValid = this.otherService.isValidNationalId(controlValue);

      return isValid ? null : { nationalCodeWrong: true };
    };
  }

  getCities(e: Event) {
    const stateId = (e.target as HTMLSelectElement).value;
    this.cities$ = this.accountService.GetCity(stateId).pipe(
      map((response: string) => JSON.parse(response)),
      catchError(() => of(false)),
    );
  }

  onFileSubmit(event) {

    if (event.strFile) {

      const reader: any = new FileReader();

      reader.readAsDataURL(event.strFile);

      reader.onload = async () => {

        var base64String = reader.result.toString();

        this.formGroup.patchValue({

          strLicenseImage: base64String.replace('data:image/jpeg;base64,', ''),

        })

      };

    }

    else {

      this.formGroup.patchValue({

        strLicenseImage: ''

      })

    }

  }


  onlyNumber(e: KeyboardEvent) {

    if (e.ctrlKey || e.metaKey || /[0-9]/.test(e.key) || ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {

      return;

    }

    e.preventDefault();
  }

  onlyPersian(e: KeyboardEvent) {

    const controlKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
      'Home',
      'End'
    ];

    if (e.ctrlKey || e.metaKey || controlKeys.includes(e.key)) {
      return;
    }

    if (!this.persianRegex.test(e.key)) {

      e.preventDefault();

    }


  }

  sanitizePersian(e: any) {
    const value = e.target.value;
    e.target.value = value.replace(/[^\u0600-\u06FF\s]/g, '');

  }

  unSubscribeAll(): void {
    this.unSubscribeAll$.next();
    this.unSubscribeAll$.complete();
  };

  ngOnDestroy(): void {
    this.unSubscribeAll();
  }


}